import {z} from 'zod';
import {toast} from 'react-toastify';
import {observer} from 'mobx-react-lite';
import React, {FC, useState} from 'react';
import {zodResolver} from '@hookform/resolvers/zod';
import {Controller, FormProvider, useForm} from 'react-hook-form';
import {VerdocsEndpoint, getOrganizationPipelineSettings, updateOrganizationPipelineSettings} from '@verdocs/js-sdk';
import {Button, FlexFill, HelpDialog, PageTitle, Spinner, Switch} from '../../Components';
import {track} from '../../Managers/Analytics';
import {AppState} from '../../AppState';

const schema = z.object({
  process_tags: z.boolean(),
  process_acroforms: z.boolean(),
  ignore_invalid_roles: z.boolean(),
  ignore_invalid_fields: z.boolean(),
});

type SchemaType = z.infer<typeof schema>;

const EMPTY: SchemaType = {process_tags: false, process_acroforms: false, ignore_invalid_roles: false, ignore_invalid_fields: false};

const Card: FC<{children: React.ReactNode; className?: string}> = ({children, className = ''}) => (
  <div className={`rounded-lg border border-[#ebecf0] bg-white p-5 ${className}`}>{children}</div>
);

const SectionTitle: FC<{children: React.ReactNode}> = ({children}) => (
  <div className="mb-3 text-[15px] font-medium text-[#33364b]">{children}</div>
);

/** One setting: what it does on the left, the switch on the right. Rows stack inside a card with a hairline between them. */
const ToggleRow: FC<{name: keyof SchemaType; title: string; children: React.ReactNode}> = ({name, title, children}) => (
  <Controller
    name={name}
    render={({field}) => (
      <label className="flex cursor-pointer items-start justify-between gap-6 border-b border-[#f2f3f5] py-3.5 first:pt-0 last:border-0 last:pb-0">
        <span>
          <span className="block text-[13px] font-medium text-[#33364b]">{title}</span>
          <span className="mt-0.5 block text-[12px] leading-relaxed text-gray-500">{children}</span>
        </span>
        <Switch
          checked={field.value}
          onCheckedChange={field.onChange}
          className="mt-0.5 data-[state=checked]:bg-[#654dcb] data-[state=unchecked]:bg-gray-200"
        />
      </label>
    )}
  />
);

export const PipelineSettings: FC = observer(() => {
  const [submitting, setSubmitting] = useState(false);
  const organizationId = AppState.currentProfile?.organization_id || '';

  const form = useForm<SchemaType>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: async () => (organizationId ? getOrganizationPipelineSettings(VerdocsEndpoint.getDefault(), organizationId) : EMPTY),
  });

  const handleSubmit = (values: SchemaType) => {
    setSubmitting(true);
    updateOrganizationPipelineSettings(VerdocsEndpoint.getDefault(), organizationId, values)
      .then(() => {
        setSubmitting(false);
        form.reset(values);
        toast.success('Settings saved');
        track('settings_updated', {section: 'document_processing'});
      })
      .catch((e) => {
        setSubmitting(false);
        toast.error('Error saving document processing settings: ' + (e.response?.data?.error || e.message));
      });
  };

  return (
    <div className="w-full max-w-[860px] pb-10">
      <PageTitle>
        Document Processing
        <FlexFill />
        <HelpDialog title="Document Processing">
          <p>
            These settings control how Verdocs turns the documents you upload into signable fields. They apply to every document processed
            for your organization, both templates and directly created envelopes.
          </p>
        </HelpDialog>
      </PageTitle>

      {form.formState.isLoading ? (
        <Spinner mode="dark" />
      ) : (
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <Card className="mb-4">
              <SectionTitle>Field detection</SectionTitle>
              <div className="mb-4 text-[13px] text-gray-500">Should we try to auto-detect fields in uploaded documents?</div>
              <ToggleRow name="process_tags" title="Process text tags">
                Scan uploaded documents for <span className="font-mono text-[11.5px]">{'{{...}}'}</span> text tags and turn them into
                fields. (Turn this off if your documents legitimately contain text that looks like a tag and should be left alone.)
              </ToggleRow>
              <ToggleRow name="process_acroforms" title="Detect fillable PDF fields">
                When a document has no text tags, find its existing fillable form fields (AcroForm widgets) and turn them into Verdocs
                fields. Handy for standard fillable PDFs, but it can produce unexpected fields when a document also has manually placed
                ones.
              </ToggleRow>
            </Card>

            <Card className="mb-4">
              <SectionTitle>Validation</SectionTitle>
              <div className="mb-4 text-[13px] text-gray-500">
                What happens when a detected tag does not add up? With both off, a bad tag stops the upload with an error so it can be fixed
                in the document.
              </div>
              <ToggleRow name="ignore_invalid_roles" title="Skip invalid roles">
                Silently skip any tag whose recipient role name is missing or does not match a role on the template.
              </ToggleRow>
              <ToggleRow name="ignore_invalid_fields" title="Skip invalid fields">
                Silently skip any tag that does not form a valid field, such as an unknown field type or a missing role.
              </ToggleRow>
            </Card>

            <div className="flex items-center gap-3">
              <Button type="submit" size="small" label="Save changes" disabled={submitting || !form.formState.isDirty} />
              <span className="text-[12px] text-gray-500">Changes apply to documents uploaded from now on.</span>
            </div>
          </form>
        </FormProvider>
      )}
    </div>
  );
});
